import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FaRegQuestionCircle } from "react-icons/fa";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 } from 'uuid';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import FilterTextBox from '../../components/FilterTextBox';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Messages, InputLimits } from '../../contexts/constants';
import { BilingualName, Errors, LanguageCode, InterestRateMaster as InterestRateMasterType } from '../../contexts/types';
import { CustomNumberInput } from '../InvestmentScreening/CustomNumberInput';
import { formatLocalDateTime, formatYearMonthByLocale } from '../../utils/utils';

/** 言語設定 */
import { supportedLngs } from '../../utils/i18n';

/**
 * マスタメンテナンス - 金利設定 画面
 */
const InterestRateMaster: React.FC = () => {
  const { t } = useTranslation();
  // Toastメッセージを表示するためのフック
  const toast = useToast();
  // WebAPIContextを使用してAPI操作を行う
  const WebAPI = useContext(WebAPIContext);

  /** タイトル */
  const TITLE = t('features.MasterMaintenance.InterestRateMaster.title');
  /** エンティティ略称 */
  const ENTITY = t('features.MasterMaintenance.InterestRateMaster.entityLabel');

  /** ヘッダー部セルスタイル */
  const headerCellStyle = {
    fontWeight: "bold",
    fontSize: "14px",
    whiteSpace: "nowrap",
    backgroundColor: "gray.100",
  };

  /** ボディ部セルスタイル */
  const bodyCellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    bgColor: "white",
  }

  /** 列固定スタイル(2列目以降はleftの設定が必要) */
  const fixedColCellStyle = {
    position: "sticky" as const,
    left: "0",
    zIndex: "1",
    boxShadow: "1px 0 0 #ccc",
  }

  /** ホバースタイル */
  const hoverStyle = {
    bg: "gray.200"
  }

  /** 項目（ラベル）スタイル */
  const labelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal" as const,
    wordBreak: "break-word" as const,
    marginTop: "8px",
    width: 170,
  }

  /** 項目（値）幅 */
  const valueWidth = 475;

  /** 金利マスタデータ */
  const [masterData, setMasterData] = useState<InterestRateMasterType[]>([]);

  /** 編集対象金利マスタ */
  const [editTarget, setEditTarget] = useState<InterestRateMasterType>({});

  /** エラー情報 */
  const [errors, setErrors] = useState<Errors>({});

  /** モーダル表示制御用：編集用モーダル */
  const { isOpen: isOpenEditModal, onOpen: onOpenEditModal, onClose: onCloseEditModal } = useDisclosure();

  /** アラートダイアログ表示制御用：Excel読み込み確認 */
  const { isOpen: isAlertOpen, onOpen: onOpenAlert, onClose: onCloseAlert } = useDisclosure();

  /** アラートダイアログ表示制御用：過去年度編集確認 */
  const { isOpen: isPastYearAlertOpen, onOpen: onOpenPastYearAlert, onClose: onClosePastYearAlert } = useDisclosure();

  /** アラートダイアログのキャンセルボタン参照 */
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pastYearCancelRef = useRef<HTMLButtonElement>(null);

  // （フィルター）地域コードを管理
  const [filterAreaCode, setFilterAreaCode] = useState<string>("");

  // 適用年度（3月期の年を入力、例: 2025 → 2025年3月期）
  const [inputFiscalYear, setInputFiscalYear] = useState<string>("");

  // 検索済みの適用年度（表示用）
  const [displayFiscalYear, setDisplayFiscalYear] = useState<string>("");

  // 適用年度文字列（YYYY-04形式）- 入力年の前年の4月として変換
  // 例: 2025年3月期 → 2024-04
  const selectedFiscalPeriod = inputFiscalYear
    ? `${Number(inputFiscalYear) - 1}-04`
    : "";

  // 編集モーダル保存ボタンの活性制御
  const [isSaving, setIsSaving] = useState(false);

  // Excel読み込み用
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importData, setImportData] = useState<InterestRateMasterType[]>([]);

  /** 使用言語のオブジェクト */
  const langs = Object.keys(supportedLngs) as LanguageCode[];

  /** 使用言語の配列 */
  const langFields = langs.join(' ');

  /** フィルター済みマスタデータ */
  const filteredMasterData = (masterData ?? []).filter(m => {
    if (!m) {
      return false;
    }

    // 指定した値が含まれているかを返す
    const hasFilterString = (propertyValue: string | null | undefined, filterString: string): boolean => {
      return (propertyValue ?? '').toLowerCase().includes(filterString.toLowerCase());
    };

    return (
      hasFilterString(m.areaCode, filterAreaCode)
    );
  });

  /**
   * 現在の会計年度を取得（4月始まり）
   */
  const getCurrentFiscalYear = useCallback(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    // 4月以降は現在年、3月以前は前年
    return currentMonth >= 4 ? currentYear : currentYear - 1;
  }, []);

  /**
   * 指定された適用年度が過去かどうかを判定
   * @param fiscalPeriod 適用年度（YYYY-04形式）
   * @returns true: 過去年度、false: 現在または未来年度
   */
  const isPastFiscalYear = useCallback((fiscalPeriod: string) => {
    if (!fiscalPeriod) return false;
    const year = parseInt(fiscalPeriod.split('-')[0], 10);
    return year < getCurrentFiscalYear();
  }, [getCurrentFiscalYear]);

  /**
   * 画面初期表示
   */
  useEffect(() => {
    // デフォルトで現在の年度+1を設定（3月期表示のため）
    // 例: 現在2024年度 → 2025年3月期として表示
    const fiscalYear = getCurrentFiscalYear();
    setInputFiscalYear(String(fiscalYear + 1));
    // displayFiscalYearは検索実行まで空のまま
  }, [getCurrentFiscalYear]);

  /**
   * 適用年度の入力チェック
   */
  const isValidFiscalYear = () => {
    return inputFiscalYear && !isNaN(Number(inputFiscalYear)) && inputFiscalYear.length === 4;
  };

  /**
   * 「新規登録」ボタン押下
   */
  const handleOpenEditModal = async () => {
    if (!isValidFiscalYear()) {
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.InterestRateMaster.message.fiscalYearInvalid'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // 新規登録時は適用年度をセット
    setEditTarget({
      fiscalPeriod: selectedFiscalPeriod,
    });
    onOpenEditModal();
  }

  /**
   * 指定したプロパティ名の値を変更
   * @param propertyName プロパティ名
   * @param value 値
   */
  const changeEditTarget = (propertyName: string, value: any) => {
    setEditTarget((prevState) => ({
      ...prevState,
      [propertyName]: value,
    }));
  }

  /**
   * 対象のマスタデータ取得
   */
  const fetchMasterData = async () => {
    if (!selectedFiscalPeriod) {
      return;
    }

    try {
      const payload = {
        query: `
          query getInterestRateMaster($hasDeleted: Boolean, $fiscalPeriod: String) {
            getInterestRateMaster(hasDeleted: $hasDeleted, fiscalPeriod: $fiscalPeriod) {
              areaCode
              fiscalPeriod
              areaName {${langFields}}
              shortTermRate
              longTermRate
              order
              deleted
              createDate
              updateDate
            }
          }`,
        variables: {
          hasDeleted: true,
          fiscalPeriod: selectedFiscalPeriod,
        }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getInterestRateMaster) {
        // 金利マスタ情報（order順にソート）
        const data = response.data.getInterestRateMaster;
        data.sort((a: InterestRateMasterType, b: InterestRateMasterType) => (a.order ?? 0) - (b.order ?? 0));
        setMasterData(data);
      } else {
        console.error(`Failed to call API getInterestRateMaster. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchListFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. fetchMasterData", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchListFailed', { name: ENTITY }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「地域コード」リンク押下
   * @param areaCode 地域コード
   * @param fiscalPeriod 適用年度
   */
  const handleEdit = async (areaCode?: string | null, fiscalPeriod?: string | null) => {
    try {
      const payload = {
        query: `
          query getInterestRateMasterById($areaCode: String!, $fiscalPeriod: String!) {
            getInterestRateMasterById(areaCode: $areaCode, fiscalPeriod: $fiscalPeriod) {
              areaCode
              fiscalPeriod
              areaName {${langFields}}
              shortTermRate
              longTermRate
              order
              deleted
              createDate
              updateDate
            }
          }`,
        variables: {
          areaCode: areaCode,
          fiscalPeriod: fiscalPeriod,
        }
      };

      // データ取得実行
      const response = await WebAPI.request(payload);

      if (response?.data?.getInterestRateMasterById) {
        // 金利マスタ情報
        setEditTarget(response?.data?.getInterestRateMasterById);
        // 編集モーダル表示
        onOpenEditModal();
      } else {
        console.error(`Failed to call API getInterestRateMasterById. errors = `, response?.errors);
        toast({
          title: TITLE,
          description: t('common.message.fetchInfoFailed', { name: ENTITY }),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error("Failed. handleEdit", error);
      toast({
        title: TITLE,
        description: t('common.message.fetchInfoFailed', { name: ENTITY }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * 「保存」ボタン押下
   */
  const handleSave = async () => {
    if (isSaving) return;

    if (!validate()) {
      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 過去年度の場合は確認ダイアログを表示
    const targetFiscalPeriod = editTarget.fiscalPeriod || selectedFiscalPeriod;
    if (isPastFiscalYear(targetFiscalPeriod)) {
      onOpenPastYearAlert();
      return;
    }

    // 保存実行
    await executeSave();
  }

  /**
   * 保存処理を実行
   */
  const executeSave = async () => {
    setIsSaving(true);

    // 登録・更新
    if (!await putEditTarget()) {
      setIsSaving(false);
      return;
    }

    // 一覧更新
    await fetchMasterData();

    // モーダル閉じる
    setIsSaving(false);
    onCloseEditModal();
  }

  /**
   * 過去年度編集確認後の保存実行
   */
  const handleConfirmPastYearSave = async () => {
    onClosePastYearAlert();
    await executeSave();
  }

  /**
   * バリデーションチェックを行う。
   * @returns true：検証成功、false：検証失敗
   */
  const validate = () => {
    setErrors({});

    const localErrors: Errors = {};

    // 必須項目チェック
    const required = (value?: string | number | null) => Boolean(value || value === 0);

    // 必須項目チェックとエラー情報設定
    const requiredAndSetError = (target: any, propertyName: string, propertyNameJp: string, errorKey: string = propertyName) => {
      const requiredResult = required(target?.[propertyName]);

      if (!requiredResult) {
        localErrors[errorKey] = {
          isError: true,
          errorMessage: t('common.message.requiredField', { fieldName: propertyNameJp }),
        };
      }
      return requiredResult;
    }

    let result = true;
    // ereaCodeのチェック
    if (!requiredAndSetError(editTarget, "areaCode", t('features.MasterMaintenance.InterestRateMaster.field.areaCode'))) {
      result = false;
    }
    // 2. areaName の動的バリデーション (ja, en等)
    langs.forEach((lang) => {
      // 最初の文字を大文字にする (例: 'ja' -> 'Ja', 'en' -> 'En')
      const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);

      // エラーキー: 既存仕様通り "areaNameJa", "areaNameEn" になる
      const errorKey = `areaName${CapitalizedLang}`;

      // 翻訳キー: 既存仕様通り 'ja' の時は "areaName"、それ以外は "areaNameEn" などを参照
      const translationKey = lang === 'ja'
        ? 'features.MasterMaintenance.InterestRateMaster.field.areaName'
        : `features.MasterMaintenance.InterestRateMaster.field.areaName${CapitalizedLang}`;

      if (!requiredAndSetError(editTarget.areaName, lang, t(translationKey), errorKey)) {
        result = false;
      }
    });
    // shortTermRateのチェック
    if (!requiredAndSetError(editTarget, "shortTermRate", t('features.MasterMaintenance.InterestRateMaster.field.shortTermRate'))) {
      result = false;
    }
    // longTermRateのチェック
    if (!requiredAndSetError(editTarget, "longTermRate", t('features.MasterMaintenance.InterestRateMaster.field.longTermRate'))) {
      result = false;
    }

    setErrors(localErrors);
    return result;
  }

  /**
   * 入力したデータを登録・更新
   */
  const putEditTarget = async () => {
    try {
      // 登録パラメータ
      const input = {
        areaCode: editTarget.areaCode,
        fiscalPeriod: editTarget.fiscalPeriod || selectedFiscalPeriod,
        // langを使用して動的に追加する
        areaName: langs.reduce((acc, lang) => {
          acc[lang] = editTarget.areaName?.[lang] || '';
          return acc;
        }, {} as BilingualName),
        shortTermRate: editTarget.shortTermRate,
        longTermRate: editTarget.longTermRate,
        order: editTarget.order || 0,
        deleted: editTarget.deleted || false,
        updateDate: editTarget.updateDate,
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
          mutation putInterestRateMaster($input: InterestRateMasterInput!) {
            putInterestRateMaster(input: $input) {status}
          }
        `,
        variables: {
          input: input,
        }
      });

      if (result.data.putInterestRateMaster.status === "success") {
        toast({
          title: TITLE,
          description: t('common.message.inputSaved'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        console.log(`Success to call API putInterestRateMaster.`);
        return true;
      } else if (result?.data?.putInterestRateMaster.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putInterestRateMaster.`);
        return false;
      } else {
        toast({
          title: TITLE,
          description: t('common.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putInterestRateMaster. errors = ${JSON.stringify(result?.errors)}`);
        return false;
      }
    } catch (error) {
      console.error("Exception to call API putInterestRateMaster. error = ", error);
      toast({
        title: TITLE,
        description: t('common.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  }

  /**
   * 「閉じる」ボタン押下
   */
  const handleClose = async () => {
    // 一覧更新
    await fetchMasterData();
    onCloseEditModal();
  }

  /**
   * 編集モーダル閉じる時、初期化する
   */
  const handleCloseComplete = () => {
    // 初期化
    setEditTarget({});
    setErrors({});
  }

  /**
   * Excelアップロードボタン押下
   */
  const handleExcelUploadClick = () => {
    if (!isValidFiscalYear()) {
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.InterestRateMaster.message.fiscalYearInvalid'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    fileInputRef.current?.click();
  }

  /**
   * Excelファイル読み込み処理
   */
  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      WebAPI.setLoading(true);

      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          if (!evt?.target?.result) return;

          const data = new Uint8Array(evt.target.result as ArrayBuffer);
          // cellNF: true でセルの書式情報（.z）を取得する（パーセンテージ書式判定に使用）
          const workbook = XLSX.read(data, { type: "array", cellNF: true });

          // 最初のシートを取得
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // シートを配列として読み込み（ヘッダー行を探すため）
          const rawData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });

          // 「地域」を含む行をヘッダー行として探す
          let headerRowIndex = 0;
          for (let i = 0; i < rawData.length; i++) {
            const row = rawData[i] as any[];
            if (row && row.some(cell => cell && String(cell).includes('地域'))) {
              headerRowIndex = i;
              break;
            }
          }

          // ヘッダー行とデータ行を取得
          const headers = rawData[headerRowIndex] as string[];
          const dataRows = rawData.slice(headerRowIndex + 1);

          // データをオブジェクト形式に変換（列インデックスも保持）
          const jsonData = dataRows
            .filter(row => row && (row as any[]).length > 0)  // 空行を除外
            .map((row, rowOffset) => {
              const obj: any = {};
              headers.forEach((header, index) => {
                if (header) {
                  obj[String(header).trim()] = (row as any[])[index];
                }
              });
              // ワークシート上の実際の行番号（0始まりのheaderRowIndex + 1始まりのデータ行 + 1でExcel行番号）
              obj.__rowIndex = headerRowIndex + 1 + rowOffset;
              return obj;
            });

          // データをInterestRateMasterType形式に変換
          // ヘッダー名からカラムを特定（複数パターンに対応）
          const getColumnValue = (row: any, patterns: string[]): string | undefined => {
            // まず完全一致を試行
            for (const pattern of patterns) {
              if (row[pattern] !== undefined) return row[pattern];
            }
            // 次に部分一致を試行（キーがパターンを含む場合のみ）
            for (const pattern of patterns) {
              const key = Object.keys(row).find(k => k.includes(pattern));
              if (key && row[key] !== undefined) return row[key];
            }
            return undefined;
          };

          /**
           * ヘッダー名のパターンから列インデックスを特定する
           * @param patterns 検索するヘッダー名パターン配列
           * @returns 列インデックス（見つからない場合は -1）
           */
          const getColumnIndex = (patterns: string[]): number => {
            // 完全一致を優先
            for (const pattern of patterns) {
              const idx = headers.findIndex(h => h && String(h).trim() === pattern);
              if (idx !== -1) return idx;
            }
            // 次に部分一致
            for (const pattern of patterns) {
              const idx = headers.findIndex(h => h && String(h).trim().includes(pattern));
              if (idx !== -1) return idx;
            }
            return -1;
          };

          /**
           * 指定セルがパーセンテージ書式かを判定する
           * xlsxのcellNF: trueで取得した書式文字列（.z）に'%'が含まれるか確認する
           * @param colIndex 列インデックス（0始まり）
           * @param rowIndex ワークシート上の行インデックス（0始まり）
           * @returns true: パーセンテージ書式
           */
          const isCellPercentFormat = (colIndex: number, rowIndex: number): boolean => {
            if (colIndex < 0) return false;
            // xlsxのセルアドレスはA1形式（例: B3）
            const cellAddress = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex });
            const cell = worksheet[cellAddress];
            return !!(cell?.z && String(cell.z).includes('%'));
          };

          /**
           * 金利値を変換する
           * パーセンテージ書式のセルは内部値が0.015等になっているため×100して登録値に変換する
           * @param value セルの生の値
           * @param colIndex 列インデックス
           * @param rowIndex ワークシート上の行インデックス
           * @returns 小数3桁に丸めた金利値
           */
          const toRateValue = (value: any, colIndex: number, rowIndex: number): number => {
            const num = Number(value || 0);
            const converted = isCellPercentFormat(colIndex, rowIndex) ? num * 100 : num;
            return parseFloat(converted.toFixed(3));
          };

          // 短期・長期金利の列インデックスを事前に特定
          const shortTermColIndex = getColumnIndex(['shortTermRate', '短期金利', '短期金利(%)', '短期', 'SHORT-TERM', 'Short-Term']);
          const longTermColIndex = getColumnIndex(['longTermRate', '長期金利', '長期金利(%)', '長期', 'LONG-TERM', 'Long-Term']);
          // 言語ごとのExcel列見出し候補（未定義言語は自動的に空配列＝空文字セットとなり、
          // SUPPORTED_LANGUAGES/supportedLngsに言語が追加されてもコード変更は不要）
          const areaNameColumnCandidates: Partial<Record<LanguageCode, string[]>> = {
            ja: ['地域', '地域名', 'areaName', 'areaNameJa'],
            // 'en' は現時点でExcel側に対応列が無いため候補なし（未定義のまま = 空配列扱い）
          };

          const importedData: InterestRateMasterType[] = jsonData.map((row: any, index: number) => {
            const orderValue = getColumnValue(row, ['order', '表示順', 'Order']);
            const rowIndex: number = row.__rowIndex;
            return {
              // Area/areaを先にチェック（「地域」との誤マッチを防ぐ）
              areaCode: String(getColumnValue(row, ['Area', 'area', 'areaCode', '地域コード']) || ''),
              fiscalPeriod: selectedFiscalPeriod,

              // 「地域」を先にチェック（完全一致優先）
              areaName: langs.reduce((acc, lang) => {
                // 現在の言語（ja, en等）に対応する列ヘッダー候補を取得
                const candidates = areaNameColumnCandidates[lang] || [];
                // Excel行から値を取得し、なければ空文字を設定
                acc[lang] = String(getColumnValue(row, candidates) || '');
                return acc;
              }, {} as BilingualName),

              // セル毎にパーセンテージ書式を判定し、該当する場合は×100して登録
              shortTermRate: toRateValue(getColumnValue(row, ['shortTermRate', '短期金利', '短期金利(%)', '短期', 'SHORT-TERM', 'Short-Term']), shortTermColIndex, rowIndex),
              longTermRate: toRateValue(getColumnValue(row, ['longTermRate', '長期金利', '長期金利(%)', '長期', 'LONG-TERM', 'Long-Term']), longTermColIndex, rowIndex),
              order: orderValue !== undefined ? Number(orderValue) : index + 1,  // データの順番で採番
              deleted: false,
            };
          });

          // バリデーション
          const validData = importedData.filter(item => item.areaCode && item.areaName);
          if (validData.length === 0) {
            toast({
              title: TITLE,
              description: t('features.MasterMaintenance.InterestRateMaster.message.excelNoData'),
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            WebAPI.setLoading(false);
            return;
          }

          setImportData(validData);
          onOpenAlert();

        } catch (err) {
          toast({
            title: t('features.MasterMaintenance.InterestRateMaster.errorTitle'),
            description: t('features.MasterMaintenance.InterestRateMaster.message.excelReadFailed'),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          console.error('Excel read error:', err);
        } finally {
          WebAPI.setLoading(false);
        }
      };
      e.target.value = '';
      reader.readAsArrayBuffer(file);
    },
    [selectedFiscalPeriod, WebAPI, toast, onOpenAlert, t, TITLE]
  );

  /**
   * Excel一括登録実行
   */
  const handleBatchImport = async () => {
    onCloseAlert();

    if (importData.length === 0) {
      return;
    }

    try {
      WebAPI.setLoading(true);

      // 一括登録API呼び出し
      const result = await WebAPI.request({
        query: `
          mutation putInterestRateMasterBatch($input: [InterestRateMasterInput]!) {
            putInterestRateMasterBatch(input: $input) {status}
          }
        `,
        variables: {
          input: importData.map(item => ({
            areaCode: item.areaCode,
            fiscalPeriod: item.fiscalPeriod,
            areaName: item.areaName,
            shortTermRate: item.shortTermRate,
            longTermRate: item.longTermRate,
            order: item.order,
            deleted: item.deleted,
          })),
        }
      });

      if (result.data.putInterestRateMasterBatch.status === "success") {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.InterestRateMaster.message.bulkRegistered', { count: importData.length }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // 一覧更新
        await fetchMasterData();
      } else {
        toast({
          title: TITLE,
          description: t('features.MasterMaintenance.InterestRateMaster.message.bulkRegisterFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putInterestRateMasterBatch. errors = ${JSON.stringify(result?.errors)}`);
      }

    } catch (error) {
      console.error("Exception to call API putInterestRateMasterBatch. error = ", error);
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.InterestRateMaster.message.bulkRegisterFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      WebAPI.setLoading(false);
      setImportData([]);
    }
  }

  /**
   * 検索ボタン押下
   */
  const handleSearch = () => {
    if (!isValidFiscalYear()) {
      toast({
        title: TITLE,
        description: t('features.MasterMaintenance.InterestRateMaster.message.fiscalYearInvalid'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setDisplayFiscalYear(inputFiscalYear);
    fetchMasterData();
  };

  /**
   * レイアウト
   */
  return (
    <>
      <Box h="100%" mt="16px">
        <Flex gap={4} align="center">
          <HStack spacing={1}>
            <Text fontWeight="bold">{t('features.MasterMaintenance.InterestRateMaster.label.fiscalYearColon')}</Text>
            <Input
              type="text"
              value={inputFiscalYear}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                setInputFiscalYear(val);
              }}
              width="80px"
              textAlign="center"
              maxLength={4}
            />
            <Text fontWeight="bold">{t('features.MasterMaintenance.InterestRateMaster.label.march')}</Text>
            <Button colorScheme="blue" onClick={handleSearch} ml={2}>
              {t('common.button.search')}
            </Button>
          </HStack>
          <Button colorScheme='blue' onClick={(e) => {
            e.currentTarget.blur();
            handleOpenEditModal()
          }}>{t('common.button.newRegistration')}</Button>
          <HStack spacing={1}>
            <Button colorScheme='green' onClick={(e) => {
              e.currentTarget.blur();
              handleExcelUploadClick();
            }}>{t('features.MasterMaintenance.InterestRateMaster.excel.uploadButton')}</Button>
            <Popover placement="bottom">
              <PopoverTrigger>
                <button style={{ background: 'none', border: 'none', padding: 0 }}>
                  <FaRegQuestionCircle size="30px" cursor="pointer" />
                </button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent width="420px" bgColor="#fffff0">
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverHeader fontWeight="bold">{t('features.MasterMaintenance.InterestRateMaster.excel.popoverHeader')}</PopoverHeader>
                  <PopoverBody>
                    <Text mb={3}>
                      {t('features.MasterMaintenance.InterestRateMaster.excel.description')}
                    </Text>
                    <Text fontWeight="bold" mb={2}>{t('features.MasterMaintenance.InterestRateMaster.excel.fileFormat')}</Text>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>{t('features.MasterMaintenance.InterestRateMaster.excel.columnName')}</Th>
                          <Th>{t('features.MasterMaintenance.InterestRateMaster.excel.columnDescription')}</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td>Area</Td>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.areaColumnDesc')}</Td>
                        </Tr>
                        <Tr>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.areaNameHeader')}</Td>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.areaNameColumnDesc')}</Td>
                        </Tr>
                        <Tr>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.shortTermHeader')}</Td>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.shortTermColumnDesc')}</Td>
                        </Tr>
                        <Tr>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.longTermHeader')}</Td>
                          <Td>{t('features.MasterMaintenance.InterestRateMaster.excel.longTermColumnDesc')}</Td>
                        </Tr>
                      </Tbody>
                    </Table>
                    <Text mt={3} fontSize="sm" color="gray.600">{t('features.MasterMaintenance.InterestRateMaster.excel.noteOrder')}</Text>
                    <Text fontSize="sm" color="gray.600">{t('features.MasterMaintenance.InterestRateMaster.excel.noteOverwrite')}</Text>
                    <Text fontSize="sm" color="gray.600">{t('features.MasterMaintenance.InterestRateMaster.excel.noteEnglishName')}</Text>
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            </Popover>
          </HStack>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
          />
        </Flex>
        <Text mt={3} mb={1} fontWeight="bold" fontSize="lg" minH="28px">
          {displayFiscalYear ? `${displayFiscalYear}${t('features.MasterMaintenance.InterestRateMaster.label.march')}` : ""}
        </Text>
        <TableContainer overflowX="auto" overflowY="auto" h="calc(100dvh - 150px)" mt={selectedFiscalPeriod ? 0 : 2}>
          <Table variant="simple" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} cellPadding={0} className="mm-ceas-table">
            <colgroup>
              <col width={250} />
              {langs.map((lang) => (
                <col key={`col-areaName-${lang}`} width={200} />
              ))}
              <col width={150} />
              <col width={150} />
              <col width={100} />
              <col width={120} />
              <col width={250} />
              <col width={250} />
            </colgroup>
            <Thead position="sticky" top={0} zIndex={2} boxShadow={`0 -1px 0 #EEEEEE`}>
              <Tr h={50}>
                <Th {...headerCellStyle} {...fixedColCellStyle}>
                  {t('features.MasterMaintenance.InterestRateMaster.field.areaCode')}
                  <FilterTextBox placeholder={t('common.placeholder.partialMatch')} filterValue={filterAreaCode} setFilterValue={setFilterAreaCode} />
                </Th>
                {langs.map((lang) => {
                  // 'ja' の時は既存仕様通り "field.areaName"、それ以外は "field.areaNameEn" などを参照（動的ループ）
                  const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                  const translationKey = lang === 'ja'
                    ? 'features.MasterMaintenance.InterestRateMaster.field.areaName'
                    : `features.MasterMaintenance.InterestRateMaster.field.areaName${CapitalizedLang}`;
                  return (
                    <Th {...headerCellStyle} key={`th-areaName-${lang}`}>
                      {t(translationKey)}
                    </Th>
                  );
                })}
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.InterestRateMaster.field.shortTermRatePct')}
                </Th>
                <Th {...headerCellStyle}>
                  {t('features.MasterMaintenance.InterestRateMaster.field.longTermRatePct')}
                </Th>
                <Th {...headerCellStyle}>{t('common.label.sortOrder')}</Th>
                <Th {...headerCellStyle}>{t('common.label.deleteFlag')}</Th>
                <Th {...headerCellStyle}>{t('common.label.createDate')}</Th>
                <Th {...headerCellStyle}>{t('common.label.updateDate')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredMasterData.map(record => {
                const bgColor = record.deleted ? "gray.100" : "white";

                return (
                  <Tr role="group" key={`${record.areaCode}-${record.fiscalPeriod}`} _hover={{ bg: "gray.200" }}>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} {...fixedColCellStyle}>
                      <Button
                        variant="link"
                        textDecoration="underline"
                        color="blue.500"
                        onClick={(e) => {
                          e.currentTarget.blur();
                          handleEdit(record.areaCode, record.fiscalPeriod)
                        }}
                        sx={{
                          display: 'inline-block',
                          maxW: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {record.areaCode}
                      </Button>
                    </Td>
                    {langs.map((lang) => (
                      <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} key={`td-areaName-${lang}-${record.areaCode}-${record.fiscalPeriod}`}>
                        {record.areaName?.[lang]}
                      </Td>
                    ))}
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.shortTermRate?.toFixed(3)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.longTermRate?.toFixed(3)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="right">{record.order}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor} textAlign="center">{record.deleted ? (
                      <Tag size="lg" colorScheme='red'>{String(record.deleted)}</Tag>
                    ) : (
                      <Tag size="lg" colorScheme='green'>{String(record.deleted)}</Tag>
                    )
                    }</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.createDate)}</Td>
                    <Td {...bodyCellStyle} _groupHover={hoverStyle} bgColor={bgColor}>{formatLocalDateTime(record.updateDate)}</Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box >

      {/* 編集モーダル */}
      <Modal isOpen={isOpenEditModal} onClose={onCloseEditModal} closeOnOverlayClick={false} onCloseComplete={handleCloseComplete} scrollBehavior="inside" size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text>{t('features.MasterMaintenance.InterestRateMaster.modal.editTitle')}</Text>
          </ModalHeader>
          <ModalBody>
            <VStack>

              <FormControl isInvalid={errors.areaCode?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.InterestRateMaster.field.areaCode')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={editTarget.areaCode || ""}
                      onChange={(event) => { changeEditTarget("areaCode", event.target.value) }}
                      placeholder={t('features.MasterMaintenance.InterestRateMaster.placeholder.areaCodeExample')}
                      width={valueWidth}
                      maxLength={InputLimits.AREA_CODE}
                      isInvalid={errors.areaCode?.isError}
                      isDisabled={!!editTarget.updateDate}
                    />
                    <FormErrorMessage mt={0}>{errors.areaCode?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.InterestRateMaster.field.fiscalYear')}
                  </FormLabel>
                  <VStack align="left">
                    <Input
                      value={formatYearMonthByLocale(editTarget.fiscalPeriod || selectedFiscalPeriod)}
                      isReadOnly
                      width={valueWidth}
                      bgColor="gray.100"
                    />
                  </VStack>
                </Flex>
              </FormControl>

              {/* 地域名（多言語）入力欄: SUPPORTED_LANGUAGES/supportedLngsに追従する動的ループ */}
              {langs.map((lang) => {
                const CapitalizedLang = lang.charAt(0).toUpperCase() + lang.slice(1);
                // エラーキー/翻訳キーは既存仕様通り "areaNameJa"/"areaNameEn" 等になる
                const errorKey = `areaName${CapitalizedLang}`;
                const fieldTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.InterestRateMaster.field.areaName'
                  : `features.MasterMaintenance.InterestRateMaster.field.areaName${CapitalizedLang}`;
                const placeholderTranslationKey = lang === 'ja'
                  ? 'features.MasterMaintenance.InterestRateMaster.placeholder.areaNameExample'
                  : `features.MasterMaintenance.InterestRateMaster.placeholder.areaName${CapitalizedLang}Example`;

                return (
                  <FormControl isInvalid={errors[errorKey]?.isError} h={70} key={`fc-areaName-${lang}`}>
                    <Flex align="start">
                      <FormLabel style={labelStyle}>
                        {t(fieldTranslationKey)}
                      </FormLabel>
                      <VStack align="left">
                        <Input
                          value={editTarget.areaName?.[lang] || ""}
                          onChange={(event) => { changeEditTarget("areaName", { ...editTarget.areaName, [lang]: event.target.value }) }}
                          placeholder={t(placeholderTranslationKey)}
                          width={valueWidth}
                          maxLength={InputLimits.AREA_NAME}
                          isInvalid={errors[errorKey]?.isError}
                        />
                        <FormErrorMessage mt={0}>{errors[errorKey]?.errorMessage}</FormErrorMessage>
                      </VStack>
                    </Flex>
                  </FormControl>
                );
              })}

              <FormControl isInvalid={errors.shortTermRate?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.InterestRateMaster.field.shortTermRatePct')}
                  </FormLabel>
                  <VStack align="left">
                    <CustomNumberInput
                      value={editTarget.shortTermRate || 0}
                      onChange={(value) => { changeEditTarget("shortTermRate", value) }}
                      placeholder="%"
                      min={-100}
                      max={100}
                      decimalPlaces={3}
                      w={valueWidth}
                      isInvalid={errors.shortTermRate?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.shortTermRate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.longTermRate?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('features.MasterMaintenance.InterestRateMaster.field.longTermRatePct')}
                  </FormLabel>
                  <VStack align="left">
                    <CustomNumberInput
                      value={editTarget.longTermRate || 0}
                      onChange={(value) => { changeEditTarget("longTermRate", value) }}
                      placeholder="%"
                      min={-100}
                      max={100}
                      decimalPlaces={3}
                      w={valueWidth}
                      isInvalid={errors.longTermRate?.isError}
                    />
                    <FormErrorMessage mt={0}>{errors.longTermRate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.sortOrder')}
                  </FormLabel>
                  <VStack align="left">
                    <CustomNumberInput
                      value={editTarget.order || 0}
                      onChange={(value) => { changeEditTarget("order", value) }}
                      placeholder={t('features.MasterMaintenance.InterestRateMaster.placeholder.sortOrder')}
                      min={0}
                      max={9999}
                      w={valueWidth}
                    />
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.deleted?.isError} h={70}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.deleteFlag')}
                  </FormLabel>
                  <VStack align="left">
                    <HStack>
                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", false);
                        }}>{t('common.label.notDeleted')}</Button>

                      <Switch
                        isChecked={editTarget.deleted}
                        onChange={(event) => { changeEditTarget("deleted", event.target.checked) }}
                        isInvalid={errors.deleted?.isError}
                      />

                      <Button fontWeight="bold" userSelect="none" cursor="pointer" variant="ghost" _hover={{ bg: 'transparent' }}
                        onClick={() => {
                          changeEditTarget("deleted", true)
                        }}>{t('common.label.deleted')}</Button>
                    </HStack>
                    <FormErrorMessage mt={0}>{errors.deleted?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.createDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.createDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.createDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.createDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

              <FormControl isInvalid={errors.updateDate?.isError}>
                <Flex align="start">
                  <FormLabel style={labelStyle}>
                    {t('common.label.updateDate')}
                  </FormLabel>
                  <VStack align="left">
                    <Text>{formatLocalDateTime(editTarget.updateDate)}</Text>
                    <FormErrorMessage mt={0}>{errors.updateDate?.errorMessage}</FormErrorMessage>
                  </VStack>
                </Flex>
              </FormControl>

            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" isDisabled={isSaving} isLoading={isSaving} onClick={(e) => {
              e.currentTarget.blur();
              handleSave();
            }} m={1}>
              {t('common.button.save')}
            </Button>
            <Button onClick={handleClose} m={1}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Excel読み込み確認ダイアログ */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text>{t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.fiscalPeriod')}: {formatYearMonthByLocale(selectedFiscalPeriod)}</Text>
              <Text>{t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.count')}: {importData.length}{t('common.label.count')}</Text>
              {isPastFiscalYear(selectedFiscalPeriod) && (
                <Text mt={4} color="red.500" fontWeight="bold">
                  {t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.pastYearWarning')}
                </Text>
              )}
              <Text mt={4}>{t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.overwrite')}</Text>
              <Text>{t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.confirmExec')}</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseAlert}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme='blue' onClick={handleBatchImport} ml={3}>
                {t('features.MasterMaintenance.InterestRateMaster.dialog.bulkImport.execRegister')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 過去年度編集確認ダイアログ */}
      <AlertDialog
        isOpen={isPastYearAlertOpen}
        leastDestructiveRef={pastYearCancelRef}
        onClose={onClosePastYearAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              {t('features.MasterMaintenance.InterestRateMaster.dialog.pastYear.title')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text color="red.500" fontWeight="bold">
                {t('features.MasterMaintenance.InterestRateMaster.dialog.pastYear.warning', { fiscalPeriod: editTarget.fiscalPeriod || selectedFiscalPeriod })}
              </Text>
              <Text mt={4}>{t('features.MasterMaintenance.InterestRateMaster.dialog.pastYear.confirmSave')}</Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={pastYearCancelRef} onClick={onClosePastYearAlert}>
                {t('common.button.cancel')}
              </Button>
              <Button colorScheme='red' onClick={handleConfirmPastYearSave} ml={3}>
                {t('features.MasterMaintenance.InterestRateMaster.dialog.pastYear.execSave')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

export default InterestRateMaster;
